import { useContext, useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  Alert,
  AlertIcon,
  Box,
  AlertDescription,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import TextFieldInput from 'components/fields/auto/TextFieldInput';
import FormInput from 'components/fields/auto/FormInput';
import DateInput from 'components/fields/auto/DateInput';
import Divider from 'views/auto_form/components/Divider';
import RadioInput from 'components/fields/auto/RadioInput';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';
import getValidationMessage from 'helpers/getValidationMessage';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  changeUnlistedVehicles,
  addOrUpdateRemark,
} from 'reducers/autoPersonalFormReducer';

import getDateString from 'helpers/getDateString';

import { SECTIONS } from 'views/auto_form/constants';

const UnlistedVehicles = () => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();

  // Color mode values for section headers
  const sectionBg = useColorModeValue('#ccc', 'gray.600');
  const sectionColor = useColorModeValue('black', 'white');

  const vehicles = useAppSelector(
    (state) => state.autoPersonalForm.data.unlisted_vehicles,
  );

  const validation = validations.find((v) => v.validation_id === '021');

  useEffect(() => {
    vehicles.forEach((vehicle, index) => {
      if (!vehicle.is_in_household) {
        dispatch(
          addOrUpdateRemark({
            validation_key: '021',
            item_key: `unlisted-vehicle-${index + 1}`,
            data: {
              text: '',
            },
          }),
        );
      } else {
        dispatch(
          addOrUpdateRemark({
            validation_key: '021',
            item_key: `unlisted-vehicle-${index + 1}`,
            data: {
              text: `Vehicle: ${
                vehicle.name
              } - Is this vehicle still in the household?: Yes - Carrier: ${
                vehicle.carrier || 'N/A'
              } - Policy #: ${vehicle.policy || 'N/A'} - Expiry Date: ${
                vehicle.expiry_date || 'N/A'
              } - Uninsured Reason: ${
                vehicle.uninsured_reason || 'N/A'
              } - Remarks: ${vehicle.remarks || 'N/A'}`,
            },
          }),
        );
      }
    });
  }, [vehicles, dispatch]);

  const onToggleChange = (index: number, status: boolean) => {
    dispatch(
      changeUnlistedVehicles({
        name: 'is_in_household',
        value: status,
        index,
      }),
    );
  };

  const onVehicleChange = (event: any, index: number) => {
    const { name, value } = event.target;

    dispatch(
      changeUnlistedVehicles({
        name,
        value,
        index,
      }),
    );
  };

  const onDateChange = (date: Date, name: string, index: number) => {
    dispatch(
      changeUnlistedVehicles({
        name,
        index,
        value: getDateString(date),
      }),
    );
  };

  if (!validation || !vehicles) return null;

  return (
    <Box mb="20px">
      <Divider mb="10px">Unlisted vehicles</Divider>

      <Alert status="warning" mb="20px" fontSize="14px">
        <AlertIcon />
        <Box w="100%">
          <AlertDescription>
            <ValidationMessage message={getValidationMessage(validation)} />
          </AlertDescription>
        </Box>
      </Alert>

      <Box pl="20px">
        {vehicles.map((vehicle, index: number) => {
          return (
            <Box mt={index !== 0 ? '20px' : ''} key={index}>
              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
              >
                <strong>({index + 1}) Vehicle: </strong>{' '}
                {`${vehicle.name}, VIN: ${vehicle.vin}`}
              </Box>

              <Box mt="10px">
                <RadioInput
                  label="Is this vehicle still in the household?"
                  subLabel={vehicle.name}
                  section={SECTIONS.VEHICLES}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(vehicle.is_in_household)}
                  onChange={(value) => onToggleChange(index, value === 'true')}
                  required
                />
              </Box>

              {vehicle.is_in_household && (
                <Box ml="20px" mt="10px">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                    mb="10px"
                  >
                    Where is the vehicle insured?
                  </Box>
                  <Flex gap="20px">
                    <FormInput
                      section={SECTIONS.VEHICLES}
                      label="Carrier"
                      name="carrier"
                      value={vehicle.carrier}
                      onChange={(e) => onVehicleChange(e, index)}
                      required={vehicle.uninsured_reason.length === 0}
                    />
                    <FormInput
                      section={SECTIONS.VEHICLES}
                      label="Policy #"
                      name="policy"
                      value={vehicle.policy}
                      onChange={(e) => onVehicleChange(e, index)}
                      required={vehicle.uninsured_reason.length === 0}
                    />
                    <DateInput
                      section={SECTIONS.VEHICLES}
                      label="Expiry Date"
                      date={getDateFromString(vehicle.expiry_date)}
                      onChange={(date: Date) =>
                        onDateChange(date, 'expiry_date', index)
                      }
                      required={vehicle.uninsured_reason.length === 0}
                    />
                  </Flex>

                  <Flex gap="20px" mt="10px">
                    <Box w="40%">
                      <TextFieldInput
                        section={SECTIONS.VEHICLES}
                        label="If vehicle is uninsured, give explanation"
                        name="uninsured_reason"
                        value={vehicle.uninsured_reason}
                        onChange={(e: Event) => onVehicleChange(e, index)}
                      />
                    </Box>

                    <Box w="60%">
                      <TextFieldInput
                        section={SECTIONS.VEHICLES}
                        label="Remarks"
                        name="remarks"
                        value={vehicle.remarks}
                        onChange={(e: Event) => onVehicleChange(e, index)}
                      />
                    </Box>
                  </Flex>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default UnlistedVehicles;
